// Fixed-tick world simulation: movement, energy, encounters, beds.
// Single sim clock (ms) drives movement AND bubble timers so Pause freezes all.
// Framework-free reducer so tests can step it without React.
import { TUNING, roomSizeForCount } from '../constants.js';
import { dist, findEncounters, nearestFreeBed, stepNpc } from '../sim/engine.js';
import type { Bed, Npc, RuntimeNpc } from '../types.js';

export interface WorldState {
  size: number;
  width?: number;
  height?: number;
  mingle?: boolean;
  now: number;
  npcs: Npc[];
  runtime: RuntimeNpc[];
  beds: Bed[];
  energyTick: Record<string, number>; // npcId -> last solitude-restore timestamp
  restTick: Record<string, number>; // npcId -> last bed-restore timestamp
  /** Sim-ms until the room may start another conversation. Missing means 0. */
  quietUntil?: number;
}

// Scatter beds clear of walls, NPC spawns, and each other (best of N tries).
export function placeBeds(size: number, runtime: RuntimeNpc[]): Bed[] {
  const m = TUNING.bedMarginPx;
  const beds: Bed[] = [];
  for (let i = 0; i < TUNING.bedCount; i++) {
    let bx = size / 2;
    let by = size / 2;
    for (let t = 0; t < 60; t++) {
      const cx = m + Math.random() * (size - 2 * m);
      const cy = m + Math.random() * (size - 2 * m);
      const clearOfNpcs = runtime.every((r) => Math.hypot(r.x - cx, r.y - cy) > 70);
      const clearOfBeds = beds.every((b) => Math.hypot(b.x - cx, b.y - cy) > 90);
      if (clearOfNpcs && clearOfBeds) {
        bx = cx;
        by = cy;
        break;
      }
    }
    beds.push({ id: `bed_${i}`, x: bx, y: by });
  }
  return beds;
}

export function initWorld(npcs: Npc[]): WorldState {
  const size = roomSizeForCount(npcs.length);
  const m = 60; // spawn margin from walls
  const runtime: RuntimeNpc[] = npcs.map((n) => ({
    npcId: n.id,
    x: m + Math.random() * (size - 2 * m),
    y: m + Math.random() * (size - 2 * m),
    dir: 'down' as const,
    moving: true,
    vx: 0,
    vy: 0,
    speedPxS: TUNING.minSpeedPxS + Math.random() * (TUNING.maxSpeedPxS - TUNING.minSpeedPxS),
    retargetAt: 0,
    busyWith: null,
    cooldownUntil: 0,
    avoidUntil: 0,
    aloneSince: 0,
    restingBed: null,
    bedSince: 0,
    wakeAt: 0,
    pushTargetId: null,
    pushUntil: 0,
  }));
  return { size, now: 0, npcs, runtime, beds: placeBeds(size, runtime), energyTick: {}, restTick: {} };
}

// Wake NPCs for a starting conversation (napper approached or push override).
// Frees beds, resets timers — the grogginess itself is captured separately
// for the LLM prompt at generation time.
export function wakeNpcs(w: WorldState, ids: string[]): WorldState {
  const set = new Set(ids);
  const restTick = { ...w.restTick };
  for (const id of set) delete restTick[id];
  return {
    ...w,
    restTick,
    runtime: w.runtime.map((r) =>
      set.has(r.npcId) ? { ...r, restingBed: null, bedSince: 0, wakeAt: 0, moving: true, retargetAt: w.now } : r,
    ),
  };
}

// Radial push-talk: npcId becomes exclusively interested in targetId for
// pushDurationS (strong seek, pairs only with target, cooldown bypassed).
// Wakes crashers — a direct order overrides sleep. Pass null to clear early.
export function applyPush(w: WorldState, npcId: string, targetId: string | null): WorldState {
  return {
    ...w,
    runtime: w.runtime.map((r) =>
      r.npcId === npcId
        ? {
            ...r,
            pushTargetId: targetId,
            pushUntil: targetId ? w.now + TUNING.pushDurationS * 1000 : 0,
            restingBed: targetId ? null : r.restingBed,
            bedSince: targetId ? 0 : r.bedSince,
            wakeAt: targetId ? 0 : r.wakeAt,
            retargetAt: targetId ? w.now : r.retargetAt,
          }
        : r,
    ),
  };
}

// Forget pushes pointing at a deleted NPC so stale ids never gate pairing.
export function clearPushRefs(w: WorldState, npcId: string): WorldState {
  return {
    ...w,
    runtime: w.runtime
      .filter((r) => r.npcId !== npcId)
      .map((r) => (r.pushTargetId === npcId ? { ...r, pushTargetId: null, pushUntil: 0 } : r)),
  };
}

export interface TickEvents {
  newPairs: Array<[string, string]>; // npcId pairs entering conversation
  busyDenied: string[]; // seeker ids whose target was busy (got avoidUntil)
}

// Advance dtMs. Mutates a draft copy; returns events.
// energyOf: lookup for steering + drain accounting (handled by caller).
// counsel: optional urge-driven seek target (wired by the UI layer).
export function tickWorld(
  w: WorldState,
  dtMs: number,
  energyOf: (id: string) => number,
  counsel?: (selfId: string, others: RuntimeNpc[]) => RuntimeNpc | null,
): { next: WorldState; events: TickEvents } {
  const now = w.now + dtMs;
  const dtS = dtMs / 1000;
  const restTick = { ...w.restTick };

  // Wake pass: hit this nap's rolled target → get up, free the bed.
  let runtime = w.runtime.map((r) => {
    if (!r.restingBed) return r;
    const target = r.wakeAt >= TUNING.bedWakeEnergy ? r.wakeAt : TUNING.bedWakeEnergy;
    if (energyOf(r.npcId) >= target) {
      delete restTick[r.npcId];
      return { ...r, restingBed: null, bedSince: 0, wakeAt: 0, moving: true, retargetAt: now };
    }
    return r;
  });

  runtime = runtime.map((r) =>
    stepNpc(r, runtime, energyOf(r.npcId), {
      size: w.size,
      width: w.width,
      height: w.height,
      mingle: w.mingle,
      onBreak: now < (w.quietUntil ?? 0),
      now,
      beds: w.beds,
      counsel,
    }, dtS),
  );

  // Arrival claims: tired NPC reaching a free bed crashes into it.
  // Sequential so two arrivals on the same tick can't share one bed.
  // Post-chat cooldown blocks crashing (too wired to sleep right after talking).
  const takenBeds = new Set(runtime.filter((r) => r.restingBed).map((r) => r.restingBed as string));
  runtime = runtime.map((r) => {
    if (r.busyWith || r.restingBed || energyOf(r.npcId) > TUNING.avoidThreshold) return r;
    if (now < r.cooldownUntil) return r;
    const bed = w.beds.find(
      (b) => !takenBeds.has(b.id) && Math.hypot(b.x - r.x, b.y - r.y) <= TUNING.bedRestRadiusPx,
    );
    if (!bed) return r;
    takenBeds.add(bed.id);
    restTick[r.npcId] = now; // first restore ticks one interval after crashing
    // Roll this nap's depth: undisturbed sleep lasts until 7-10 energy.
    const wakeAt = TUNING.bedWakeEnergy + Math.floor(Math.random() * 4);
    return { ...r, restingBed: bed.id, bedSince: now, wakeAt, moving: false, vx: 0, vy: 0 };
  });

  // Do-not-disturb set for pairing:
  // - bed-seekers homing on a free bed (they'd otherwise chat-loop at
  //   encounter range and never reach the crash radius),
  // - crashers too exhausted to talk (energy < 3: bed is primary until
  //   they recover to at least 3). Push-talk bypasses all of this.
  const dnd = new Set<string>();
  for (const r of runtime) {
    if (r.busyWith) continue;
    const e = energyOf(r.npcId);
    if (r.restingBed) {
      if (e < 3) dnd.add(r.npcId);
      continue;
    }
    if (e > TUNING.avoidThreshold) continue;
    if (nearestFreeBed(r, runtime, w.beds, TUNING.bedSeekRadiusPx)) dnd.add(r.npcId);
  }

  // Room break blocks new pairs. An active push still scans: findEncounters
  // already lets push-talk bypass cooldown.
  const pushActive = runtime.some((r) => !!r.pushTargetId && now < r.pushUntil);
  const pairs = now < (w.quietUntil ?? 0) && !pushActive
    ? []
    : findEncounters(runtime, now, dnd);
  const claimed = new Set<string>();
  const newPairs: Array<[string, string]> = [];
  const rt = new Map(runtime.map((r) => [r.npcId, { ...r }]));
  for (const [a, b] of pairs) {
    if (claimed.has(a.npcId) || claimed.has(b.npcId)) continue;
    const ra = rt.get(a.npcId)!;
    const rb = rt.get(b.npcId)!;
    ra.busyWith = b.npcId;
    rb.busyWith = a.npcId;
    ra.moving = false;
    rb.moving = false;
    claimed.add(a.npcId);
    claimed.add(b.npcId);
    newPairs.push([a.npcId, b.npcId]);
  }

  // Busy-denied: free NPC in range of a busy one → 30s backoff (steering only).
  const busyDenied: string[] = [];
  for (const r of rt.values()) {
    if (r.busyWith || r.restingBed || now < r.cooldownUntil || now < r.avoidUntil) continue;
    for (const o of rt.values()) {
      if (o.npcId === r.npcId || !o.busyWith) continue;
      if (dist(r, o) <= TUNING.encounterDistPx) {
        r.avoidUntil = now + TUNING.busyAvoidS * 1000;
        busyDenied.push(r.npcId);
        break;
      }
    }
  }

  runtime = [...rt.values()];
  return { next: { ...w, now, runtime, restTick }, events: { newPairs, busyDenied } };
}

// Solitude restore: +1 energy per 10s alone (not talking/resting, no NPC in range).
export function solitudeRestore(
  w: WorldState,
  energyOf: (id: string) => number,
  setEnergy: (id: string, v: number) => void,
): void {
  for (const r of w.runtime) {
    if (r.busyWith || r.restingBed) continue;
    const near = w.runtime.some(
      (o) => o.npcId !== r.npcId && dist(r, o) <= TUNING.encounterDistPx,
    );
    if (near) continue;
    // Never tracked → alone since world start (t=0), not since now.
    const last = w.energyTick[r.npcId] ?? 0;
    if (w.now - last >= TUNING.solitudeRestoreEveryS * 1000) {
      const e = energyOf(r.npcId);
      setEnergy(r.npcId, Math.min(10, e + TUNING.solitudeRestoreAmount));
      w.energyTick[r.npcId] = w.now;
    }
  }
}

// Bed restore: +1 energy per 2.5s while crashed (4x solitude rate).
// Claim site stamps restTick, so the first tick lands one interval in.
export function restRestore(
  w: WorldState,
  energyOf: (id: string) => number,
  setEnergy: (id: string, v: number) => void,
): void {
  for (const r of w.runtime) {
    if (!r.restingBed) continue;
    const last = w.restTick[r.npcId] ?? w.now;
    if (w.now - last >= TUNING.bedRestoreEveryS * 1000) {
      setEnergy(r.npcId, Math.min(10, energyOf(r.npcId) + 1));
      w.restTick[r.npcId] = w.now;
    }
  }
}
